/**
 * ResearchDetail - Detail view for a research item with ManifestValueLabel.
 */

import { AlertCircle, Clock } from '@untitledui/icons';
import { useCallback, useMemo } from 'react';

import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';
import { DateTimeValueLabel } from '@/lib/crm/components/properties/labels/DateTimeValueLabel';
import { ManifestValueLabel } from '@/lib/crm/components/properties/labels/ManifestValueLabel';
import type { CrmObject } from '@/lib/crm/core/types';
import type { ViewComponent } from '@/types/dynamic_views';

import { ResearchNeedsMoreInfo } from './ResearchNeedsMoreInfo';
import { getResearchOutcome, researchRecordTypeFor } from './researchOutcome';
import type { Research } from './useResearch';

/**
 * Recursively check whether any conversation_starter component exists at any
 * nesting depth. Used to short-circuit filtering when it would be a no-op.
 */
function hasConversationStarter(components: ViewComponent[]): boolean {
  for (const c of components) {
    if (c.type === 'conversation_starter') {
      return true;
    }
    if (c.children !== undefined && hasConversationStarter(c.children)) {
      return true;
    }
  }
  return false;
}

/**
 * Recursively remove conversation_starter components and parent sections
 * that become empty as a result. Returns a new array; does not mutate input.
 */
function stripConversationStarters(
  components: ViewComponent[]
): ViewComponent[] {
  const result: ViewComponent[] = [];
  for (const c of components) {
    if (c.type === 'conversation_starter') {
      continue;
    }
    if (c.children !== undefined) {
      const newChildren = stripConversationStarters(c.children);
      if (c.type === 'section' && newChildren.length === 0) {
        continue;
      }
      result.push({ ...c, children: newChildren });
    } else {
      result.push(c);
    }
  }
  return result;
}

interface ResearchDetailProps {
  /** Research item to display */
  research: Research;
  /** CRM object - optional, only passed when interaction is desired */
  object?: CrmObject;
  /** Callback when compose email interaction is triggered */
  onComposeEmail?: (data: {
    component_id: string;
    subject: string;
    body: string;
    context_label: string;
  }) => void;
  /** Callback when user wants to open an existing proposal */
  onOpenProposal?: (proposalId: number) => void;
  /** Whether a proposal is currently being created */
  isComposing?: boolean;
}

/**
 * Loading skeleton for research in progress.
 */
function LoadingSkeleton() {
  return (
    <div className='space-y-4 animate-pulse'>
      <div className='flex items-center gap-2 text-tertiary'>
        <Clock className='size-4' />
        <span className='text-sm'>Research in progress…</span>
      </div>
      <div className='space-y-3'>
        <div className='h-4 bg-secondary rounded w-3/4' />
        <div className='h-4 bg-secondary rounded w-1/2' />
        <div className='h-4 bg-secondary rounded w-5/6' />
        <div className='h-4 bg-secondary rounded w-2/3' />
      </div>
    </div>
  );
}

/**
 * Queued state display.
 */
function QueuedState() {
  return (
    <div className='flex flex-col items-center justify-center text-center py-8'>
      <div className='w-10 h-10 rounded-full bg-utility-gray-100 flex items-center justify-center mb-3'>
        <Clock className='size-5 text-utility-gray-500' />
      </div>
      <p className='text-sm text-secondary'>
        Research is queued and will start shortly.
      </p>
    </div>
  );
}

/**
 * Error state display — reserved for genuine failures (provider outage, AI
 * synthesis error, crashed run). A run that simply had too little to go on is
 * not an error; see `ResearchNeedsMoreInfo`.
 */
function ErrorState({ errorMessage }: { errorMessage: string | null }) {
  return (
    <div className='p-4 bg-utility-error-50 border border-utility-error-200 rounded-lg'>
      <div className='flex items-start gap-3'>
        <div className='w-8 h-8 rounded-full bg-utility-error-100 flex items-center justify-center flex-shrink-0'>
          <AlertCircle className='size-4 text-utility-error-600' />
        </div>
        <div className='flex-1 min-w-0'>
          <h4 className='text-sm font-semibold text-utility-error-700 mb-1'>
            Research failed
          </h4>
          <p className='text-sm text-utility-error-600'>
            {errorMessage || 'An unknown error occurred during research.'}
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * Component for displaying detailed research results using DynamicViewRenderer.
 */
export function ResearchDetail({
  research,
  object,
  onComposeEmail,
  onOpenProposal,
  isComposing,
}: ResearchDetailProps) {
  const handleInteraction = useCallback(
    (interactionType: string, data: unknown) => {
      if (interactionType === 'compose_email' && onComposeEmail) {
        // Type guard for compose_email interaction data
        const typedData = data as Record<string, unknown>;
        if (
          !data ||
          typeof data !== 'object' ||
          typeof typedData.component_id !== 'string' ||
          typeof typedData.subject !== 'string' ||
          typeof typedData.body !== 'string' ||
          typeof typedData.context_label !== 'string'
        ) {
          console.error('Invalid compose_email interaction data', { data });
          return;
        }
        onComposeEmail({
          component_id: typedData.component_id,
          subject: typedData.subject,
          body: typedData.body,
          context_label: typedData.context_label,
        });
      } else if (interactionType === 'open_proposal' && onOpenProposal) {
        // Type guard for open_proposal interaction data
        const typedData = data as Record<string, unknown>;
        if (
          !data ||
          typeof data !== 'object' ||
          typeof typedData.proposal_id !== 'number'
        ) {
          console.error('Invalid open_proposal interaction data', { data });
          return;
        }
        onOpenProposal(typedData.proposal_id);
      }
    },
    [onComposeEmail, onOpenProposal]
  );

  const displayedValue = useMemo(() => {
    // Pass through when compose-email is available or no view definition exists
    if (onComposeEmail || !research.view_definition_json) {
      return research.view_definition_json;
    }
    try {
      const parsed = JSON.parse(research.view_definition_json) as {
        version: string;
        components?: ViewComponent[];
        [key: string]: unknown;
      };
      if (!parsed.components || !Array.isArray(parsed.components)) {
        return research.view_definition_json;
      }
      // No-op when there are no conversation_starter components to strip —
      // return the original JSON string so ManifestValueLabel's string-handling
      // path is preserved unchanged.
      if (!hasConversationStarter(parsed.components)) {
        return research.view_definition_json;
      }
      return {
        ...parsed,
        components: stripConversationStarters(parsed.components),
      };
    } catch {
      // Malformed JSON — fall back to original; ManifestValueLabel handles parse errors
      return research.view_definition_json;
    }
  }, [research.view_definition_json, onComposeEmail]);

  // Handle queued state
  if (research.status === 'queued') {
    return <QueuedState />;
  }

  // Handle in-progress state
  if (research.status === 'in_progress') {
    return <LoadingSkeleton />;
  }

  // Handle failed state. "Too little to go on" is a guidance state, not an
  // error — only a genuine failure gets the error treatment.
  if (research.status === 'failed') {
    if (getResearchOutcome(research) === 'needs_more_info') {
      return (
        <ResearchNeedsMoreInfo recordType={researchRecordTypeFor(object)} />
      );
    }
    return <ErrorState errorMessage={research.error_message} />;
  }

  // Handle complete state with view_definition
  if (research.status === 'complete' && research.view_definition_json) {
    return (
      <div>
        <ManifestValueLabel
          value={displayedValue}
          onInteraction={
            (onComposeEmail || onOpenProposal) && !isComposing
              ? handleInteraction
              : undefined
          }
          isComposing={isComposing}
        />
        {research.completed_at && (
          <p className='mt-4 text-xs text-tertiary'>
            Completed{' '}
            <DateTimeValueLabel value={research.completed_at} fontSize='xs' />
          </p>
        )}
      </div>
    );
  }

  // Fallback: show summary if available but no view_definition
  if (research.summary) {
    return (
      <div>
        <MarkdownContent
          content={research.summary}
          orgId={object?.org_id}
          className='text-sm'
        />
        {research.completed_at && (
          <p className='mt-4 text-xs text-tertiary'>
            Completed{' '}
            <DateTimeValueLabel value={research.completed_at} fontSize='xs' />
          </p>
        )}
      </div>
    );
  }

  // No content available
  return (
    <div className='text-center py-8'>
      <p className='text-sm text-tertiary'>No research results available.</p>
    </div>
  );
}
